import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import dayjs, { Dayjs } from 'dayjs';
import { RequestStateEnum } from '../../../../enums/app.enum';
import { DayjsFormatPipe } from '../../../../pipes/date.pipe';
import { ModalService } from '../../../../services/modal.service';
import { ActivityStore } from '../../../../stores/activity.store';
import { ButtonComponent } from '../../../atoms/button/button.component';
import { CardHeaderComponent } from '../../../atoms/card-header/card-header.component';
import { CardComponent } from '../../../atoms/card/card/card.component';
import { IconComponent } from '../../../atoms/icon/icon.component';
import { NoEntriesPlaceholderComponent } from '../../../molecules/no-entries-placeholder/no-entries-placeholder.component';
import { PaginatorComponent } from '../../../molecules/paginator/paginator.component';
import { PaginatorIntervalTypeEnum } from '../../../molecules/paginator/paginator.enum';
import { activityTypeLabels } from '../../../organisms/activities/activities.constants';
import { Activity, ActivityTypeEnum } from '../../../organisms/activities/activities.types';
import { MainLayoutComponent } from '../../../organisms/main-layout/main-layout.component';
import { ModalCreateEditActivityComponent } from '../../../organisms/modal/modal-create-edit-activity/modal-create-edit-activity.component';

@Component({
  selector: 'app-activities',
  imports: [
    CardComponent,
    CardHeaderComponent,
    NoEntriesPlaceholderComponent,
    MainLayoutComponent,
    PaginatorComponent,
    MatPaginatorModule,
    DayjsFormatPipe,
    IconComponent,
    ButtonComponent,
  ],
  templateUrl: './activities.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivitiesComponent {
  protected PaginatorIntervalTypeEnum = PaginatorIntervalTypeEnum;
  protected RequestStateEnum = RequestStateEnum;

  private readonly activityStore = inject(ActivityStore);
  private readonly modalService = inject(ModalService);

  private readonly loadActivities = effect(() =>
    this.fetchActivities(this.start(), this.end(), this.pageSize(), this.pageIndex())
  );

  readonly requestState = this.activityStore.requestState;
  readonly activities = this.activityStore.entities;
  readonly start = signal<Dayjs>(dayjs().startOf('week'));
  readonly end = signal<Dayjs>(dayjs().endOf('week'));
  readonly total = signal<number>(0);
  readonly pageSize = signal<number>(10);
  readonly pageIndex = signal<number>(0);

  public getActivityTypeLabel(type: ActivityTypeEnum): string {
    return activityTypeLabels[type];
  }

  public handlePageEvent(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
  }

  public handleTimeIntevalEvent(event: { index: number; year: number }): void {
    const start = dayjs().year(event.year).isoWeek(event.index).startOf('week');
    const end = dayjs().year(event.year).isoWeek(event.index).endOf('week');

    this.start.set(start);
    this.end.set(end);
  }

  public openEditActivityModal(activity: Activity): void {
    this.modalService.openOverlayWithConfig(ModalCreateEditActivityComponent, { activity });
  }

  public openCreateActivityModal(): void {
    this.modalService.openOverlayWithConfig(ModalCreateEditActivityComponent, { activity: null });
  }

  private fetchActivities(start: Dayjs, end: Dayjs, pageSize: number, pageIndex: number): void {
    this.activityStore.loadInRange(start, end, pageSize, pageIndex);
  }
}
